<template>
  <UIDialog :is-open :zIndex :title size="lg" flat @close="handleClose">
    <template v-if="isChanges" #title>
      <div class="module-rules-form__title">
        <span>Preview changes</span>
        <span v-if="criteria" class="module-rules-form__criteria">If {{ criteria }}</span>
      </div>
    </template>
    <RulesForm
      :rule
      :is-changes
      :get-filled-actions
      :prefilled
      :disabled
      @fetch:transactions="handleFetchTransactions"
      @create="handleCreate"
      @update="handleUpdate"
      @apply="handleApply"
      @close="handleClose"
    >
      <template #actions="{ actions, updateActions }">
        <MappingRulesActions
          :model-value="actions"
          :json-data
          :disabled
          @update:model-value="updateActions"
        />
      </template>
      <template #change="{ list, actions }">
        <Change v-for="item in list" :key="item.id" :item :actions :json-data />
      </template>
    </RulesForm>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import {
  LinkedDataTransaction,
  ModalClass,
  PaginatedResponse,
  TransactionRule,
  RuleApplyStatus
} from '@types'
import { MappingRuleAction } from './utils/types'

import { PAGE_SIZE } from '@/const'
import { NOTIFICATION_DELAY } from '@/const/common'

import { useNotifications } from '@/plugins/notification'

import { mapChangeCriteria } from '@/modules/Rules/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { getRuleCreationStatusMessage, getRuleCreationNotificationType, getRuleApplyStatusMessage, getRuleApplyNotificationType } from '@/store/linkedData/utils/helpers'

import { useLinkedDataMappingRulesStore } from '@/store/linkedData/rules/mapping'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import RulesForm from '@/modules/Rules/RulesForm.vue'
import Change from './components/Change.vue'
import MappingRulesActions from './MappingRulesActions.vue'
import { UIDialog } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
  rule?: TransactionRule<MappingRuleAction[]>
  isChanges?: boolean
  jsonData?: Record<string, any>
  prefilled?: boolean
}

type Emits = {
  fetch: []
  update: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const linkedDataMappingRulesStore = useLinkedDataMappingRulesStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const data = computed(() => linkedDataMappingRulesStore.getList)

const title = computed(() => (props.rule?.id ? 'Edit rule' : 'New rule'))

const criteria = computed(
  () => props.rule && mapChangeCriteria(props.rule.data.criteria),
)

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const disabled = computed(() => linkedDataMappingRulesStore.loadingAction)

const getFilledActions = (actions: MappingRuleAction[]) => {
  return actions.filter(action => {
    if (typeof action === 'object') {
      return action && Object.values(action).some(v => !!v)
    }
    return !!action
  })
}

const handleFetchTransactions = async (
  data: TransactionRule<MappingRuleAction[]>['data'],
  pageNo: number,
  callback: (data: PaginatedResponse<LinkedDataTransaction>) => void,
  signal?: AbortSignal,
) => {
  try {
    const result = await linkedDataMappingRulesStore.fetchTransactions(
      data,
      pageNo,
      PAGE_SIZE,
      signal,
    )
    callback(result)
  } catch (e) {
    handleCatchedError(e as string, { data, pageNo })
  }
}

const handleCreate = async (
  ruleData: TransactionRule<MappingRuleAction[]>['data'],
  apply_to_existing_transactions: boolean,
  successCallback: (customMessage?: string) => void,
  errorCallback: () => void,
) => {
  try {
    const order = data.value.at(-1)?.order || 0
    const createdRule = await linkedDataMappingRulesStore.store({
      order: order + 1,
      data: ruleData,
      apply_to_existing_transactions,
    })

    // Get appropriate message and notification type based on apply_status
    const message = getRuleCreationStatusMessage(createdRule?.apply_status)
    const notificationType = getRuleCreationNotificationType(createdRule?.apply_status)

    // Handle different status types appropriately for rule creation
    if (notificationType === 'success') {
      // For success or no apply status, use the success callback with custom message
      successCallback(message)
    } else {
      // For warnings/errors, clear progress and show custom notification
      errorCallback()

      // Show custom notification with appropriate type
      const { [notificationType]: showNotification } = useNotifications()
      await showNotification({ message }, NOTIFICATION_DELAY)
    }

    emit('fetch')
    handleClose()
  } catch {
    errorCallback()
  }
}

const handleUpdate = async (
  id: string,
  ruleData: TransactionRule<MappingRuleAction[]>,
  successCallback: () => void,
  errorCallback: () => void,
) => {
  try {
    await linkedDataMappingRulesStore.update(id, ruleData)
    successCallback()
  } catch (e) {
    errorCallback()
    handleCatchedError(e as string, ruleData)
  }
}

const handleApply = async (
  ruleData: TransactionRule<MappingRuleAction[]>['data'],
  successCallback: (customMessage?: string) => void,
  errorCallback: () => void,
) => {
  try {
    const result = await linkedDataMappingRulesStore.apply(ruleData)

    // Get the appropriate message and notification type
    const message = getRuleApplyStatusMessage(result.apply_status)
    const notificationType = getRuleApplyNotificationType(result.apply_status)

    // Handle different status types appropriately
    if (result.apply_status === RuleApplyStatus.SUCCESS) {
      // For success, use the success callback with custom message
      successCallback(message)
      emit('fetch')
      handleClose()
    } else {
      // For non-success statuses, clear progress and show custom notification
      errorCallback()

      // Show custom notification with appropriate type
      const { [notificationType]: showNotification } = useNotifications()
      await showNotification({ message }, NOTIFICATION_DELAY)

      // Refresh data but don't close modal for failed/partial/not_applied
      emit('fetch')
    }
  } catch {
    errorCallback()
  }
}

const handleClose = () => {
  props.modal.close()
}

onMounted(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>
